import { RootState, useAppDispatch } from '@app/store'
import { getPasswordVerificationLogin } from '@app/store/passwordVerification/actions'
import { rem } from '@app/theme/materialUITheme'
import { Box, Typography, Paper, InputBase, Button, Theme, Toolbar } from '@mui/material'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { PasswordVerificationState } from 'types/passwordVerification'
import { FormattedMessageWrapper } from '../ui/FormattedMessageWrapper'
import { ErrorPayload } from '@app/store/passwordVerification/sagas'
import AlertCircleIcon from '@app/assets/images/icons/alert-circle'
import { LanguageSwitcher } from '../layout/LanguageSwitcher'
import makeStyles from '@mui/styles/makeStyles'
import { PasswordToggleIcon } from '@app/assets/images/icons/passwordToggleIcon'
import { useIntl } from 'react-intl'

const useStyles = makeStyles((theme: Theme) => ({
  toolbar: {
    maxWidth: 1920,
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: rem(100),
    boxSizing: 'border-box',
    alignItems: 'unset',
    paddingTop: rem(40),
    [theme.breakpoints.down('sm')]: {
      padding: `${rem(10)} ${rem(10)}`,
    },
  },
  leftCol: {
    alignSelf: 'center',
    display: 'flex',
  },
  rightCol: {
    alignSelf: 'flex-start',
  },
  colWrap: {
    flexDirection: 'row',
    display: 'flex',
  },
}))

const LockedTournamentLanding = () => {
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const tournamentId = Number(useParams().tournamentId) ?? ''
  const defaultBackgroundImage = '/images/locked-tournament-background.png'
  const passwordVerificationState: PasswordVerificationState = useSelector(
    (state: RootState) => state.passwordVerificationReducer,
  )
  const intl = useIntl()

  const [passwordFieldType, setPasswordFieldType] = useState<'password' | 'text'>('password')
  const [password, setPassword] = useState('')
  const landingBackgroundImage = passwordVerificationState.heroImage?.url || defaultBackgroundImage

  const handlePasswordSubmit = () => {
    const formattedPassword = password.replace(/\s/g, '')
    if (!formattedPassword || !tournamentId) {
      return
    }
    dispatch(getPasswordVerificationLogin(tournamentId, formattedPassword))
  }

  const togglePasswordFieldType = () => {
    const newPasswordFieldType = passwordFieldType === 'password' ? 'text' : 'password'
    setPasswordFieldType(newPasswordFieldType)
  }

  if (!passwordVerificationState.passwordVerification) {
    return null
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100dvh',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundImage: `url(${landingBackgroundImage})`,
      }}
    >
      <Box
        sx={{
          width: '100%',
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(0deg,rgba(23, 23, 23, 0.60) 0%,rgba(23, 23, 23, 0.60) 100%)',
          backdropFilter: `blur(${rem(6)})`,
          WebkitBackdropFilter: `blur(${rem(6)})`,
        }}
      >
        <Toolbar className={classes.toolbar}>
          <div className={classes.leftCol} />
          <div className={classes.rightCol}>
            <div className={classes.colWrap}>
              <LanguageSwitcher />
            </div>
          </div>
        </Toolbar>
        <Paper
          elevation={6}
          sx={{
            position: 'relative',
            p: 5,
            margin: '10% auto',
            borderRadius: 4,
            background: 'white',
            boxShadow: '0px 25px 50px -12px rgba(0, 188, 125, 0.10)',
            outline: '1px rgba(255, 255, 255, 0.50) solid',
            outlineOffset: '-1px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            maxWidth: rem(450),
            wordBreak: 'break-word',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              background: '#F0FAF6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            {/* Lock Icon SVG */}
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="6" y="12" width="16" height="10" rx="3" stroke="#098F50" strokeWidth="2.33" />
              <path
                d="M9 12V9C9 6.23858 11.2386 4 14 4C16.7614 4 19 6.23858 19 9V12"
                stroke="#098F50"
                strokeWidth="2.33"
              />
            </svg>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <Typography
              sx={{
                fontFamily: 'Exo',
                fontStyle: 'italic',
                fontSize: rem(32),
                fontWeight: 800,
                textTransform: 'uppercase',
                textAlign: 'center',
                color: '#555',
                lineHeight: 1,
              }}
            >
              {passwordVerificationState.tournamentName}
            </Typography>
            <Typography
              sx={{
                color: '#525252',
                fontFamily: 'Roboto',
                fontWeight: 400,
                textAlign: 'center',
                fontSize: rem(18),
              }}
            >
              {passwordVerificationState.courseName} ·{' '}
              {passwordVerificationState.startTime
                ?.setLocale(passwordVerificationState.units === 'us' ? 'en-US' : 'de-DE')
                .toLocaleString(DateTime.DATE_SHORT)}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: '#171717',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            <FormattedMessageWrapper id="lockedTournament.enterPassword" />
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <Box
              sx={{
                width: '100%',
                paddingBottom: passwordVerificationState.error === ErrorPayload.INCORRECT_PASSWORD ? 0 : rem(4),
              }}
            >
              <InputBase
                type={passwordFieldType}
                fullWidth
                placeholder={intl.formatMessage({ id: 'lockedTournament.passwordPlaceholder' })}
                error={passwordVerificationState.error === ErrorPayload.INCORRECT_PASSWORD}
                sx={{
                  height: 48,
                  px: 2,
                  background: 'white',
                  borderRadius: 2,
                  border:
                    passwordVerificationState.error === ErrorPayload.INCORRECT_PASSWORD
                      ? '1px solid #D2080F'
                      : '1px solid #E5E5E5',
                  color: '#171717',
                  fontSize: 15,
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                inputProps={{
                  'aria-label': 'enter password',
                  onKeyUp: (e) => e.key === 'Enter' && handlePasswordSubmit(),
                }}
                disabled={passwordVerificationState.isLoading}
                endAdornment={
                  <div style={{ display: 'flex', alignItems: 'center', gap: rem(4) }}>
                    <button
                      type="button"
                      aria-label="Toggle password visibility"
                      aria-pressed={passwordFieldType === 'text'}
                      onClick={togglePasswordFieldType}
                      disabled={passwordVerificationState.isLoading}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: 0,
                        margin: 0,
                        border: 'none',
                        background: 'transparent',
                        cursor: passwordVerificationState.isLoading ? 'default' : 'pointer',
                      }}
                    >
                      <PasswordToggleIcon
                        style={{ cursor: passwordVerificationState.isLoading ? 'default' : 'pointer' }}
                        onClick={togglePasswordFieldType}
                      />
                    </button>
                    {passwordVerificationState.error === ErrorPayload.INCORRECT_PASSWORD && <AlertCircleIcon />}
                  </div>
                }
              />

              {passwordVerificationState.error === ErrorPayload.INCORRECT_PASSWORD && (
                <Typography color="error" sx={{ paddingTop: rem(4) }}>
                  <FormattedMessageWrapper id="lockedTournament.incorrectPassword" />
                </Typography>
              )}
            </Box>
            <Button
              fullWidth
              variant="contained"
              sx={(theme) => ({
                background: password.length ? '#098F50' : '#E5E5E5',
                color: password.length ? 'white' : '#A1A1A1',
                fontWeight: 500,
                fontSize: 15,
                borderRadius: 2,
                py: 1.5,
                boxShadow: 'none',
                '&:hover': { background: `${theme.customPalette.ggbGreen}` },
              })}
              disabled={!password || passwordVerificationState.isLoading}
              onClick={handlePasswordSubmit}
            >
              <FormattedMessageWrapper id="lockedTournament.viewTournament" />
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}

export default LockedTournamentLanding
